import { useMemo } from 'react';
import { useBill } from '../bill.tsx';
import { useStore } from '../store.tsx';
import { useToast } from '../ui.tsx';
import type { DiscountButton, Product } from '../lib/types.ts';

/** จำนวนผลค้นหาสูงสุด — ★ ของเดิมตัดที่ 50 ★ ค่านี้มีผลกับสิ่งที่พนักงานเห็น */
const SEARCH_LIMIT = 50;

/**
 * กริดสินค้า
 *
 * ★ ลอกจาก components/ProductList.js ★
 *   · ช่อง 100×130 · รูป 100×100 · ชื่อ 12px ใต้รูป · ห่างขวาและล่าง 10px
 *   · เรียงต่อกันแล้ววนบรรทัดเอง (wrap) เลื่อนยาวต่อเนื่อง ★ ไม่มีการแบ่งหน้า ★
 *   · ★ ค้นหาแล้วข้ามหมวดทั้งหมด ★ ไม่ใช่ค้นเฉพาะในหมวดที่เลือกอยู่
 *   · ค้นจากรหัสหรือชื่อแบบ substring ตัดที่ 50 รายการ
 *
 * ★ ตำแหน่งของทุกเมนูมาจาก sortOrder ที่ยกมาจากระบบเดิม ★
 * พนักงานจำตำแหน่งเหล่านี้ได้ ห้ามเรียงใหม่ตามตัวอักษรหรือตามราคา
 */
export function ProductGrid({
  groupId,
  searchText,
}: {
  groupId: number | null;
  searchText: string;
}) {
  const store = useStore();
  const bill = useBill();
  const toast = useToast();

  const items = useMemo(() => {
    const term = searchText.trim();

    // ปุ่มส่วนลดอยู่ปนในกริดเดียวกับสินค้า และเรียงด้วย sortOrder ชุดเดียวกัน
    type Cell =
      | { kind: 'product'; sortOrder: number; product: Product }
      | { kind: 'discount'; sortOrder: number; button: DiscountButton };

    const cells: Cell[] = [
      ...store.products.map((p) => ({ kind: 'product' as const, sortOrder: p.sortOrder, product: p })),
      ...store.discountButtons.map((d) => ({
        kind: 'discount' as const, sortOrder: d.sortOrder, button: d,
      })),
    ];

    const matching = term
      ? cells
          .filter((c) => {
            const code = c.kind === 'product' ? c.product.code : c.button.code;
            const name = c.kind === 'product' ? c.product.name : c.button.name;
            return code.includes(term) || name.includes(term);
          })
          .slice(0, SEARCH_LIMIT)
      : cells.filter((c) => {
          if (groupId === null) return true;
          const id = c.kind === 'product' ? c.product.productGroupId : c.button.productGroupId;
          return id === groupId;
        });

    return matching.sort((a, b) => a.sortOrder - b.sortOrder);
  }, [store.products, store.discountButtons, groupId, searchText]);

  const inCart = (productId: number) =>
    bill.cart.filter((i) => i.product.id === productId).reduce((n, i) => n + i.qty, 0);

  const tap = (cell: (typeof items)[number]) => {
    if (bill.payload.billType === '') {
      toast('ยังไม่ได้สร้างบิล', 'warn');
      return;
    }
    if (cell.kind === 'product') bill.addProduct(cell.product);
    else bill.applyDiscountButton(cell.button);
  };

  return (
    <div className="product-wrap">
      {items.map((cell) => {
        const key = cell.kind === 'product' ? `p${cell.product.id}` : `d${cell.button.id}`;
        const name = cell.kind === 'product' ? cell.product.name : cell.button.name;
        const image = cell.kind === 'product' ? cell.product.imageUrl : cell.button.imageUrl;
        const count = cell.kind === 'product' ? inCart(cell.product.id) : 0;

        return (
          <button
            key={key}
            className={`product-cell${cell.kind === 'discount' ? ' discount' : ''}`}
            onClick={() => tap(cell)}
          >
            {/* รูปที่ไม่มีจะใช้รูปแทนของแอปเดิม ไม่ปล่อยเป็นช่องว่าง */}
            <img src={image ?? '/ui/default.png'} alt="" loading="lazy" />
            <span className="pname">{name}</span>
            {count > 0 && <span className="incart">{count}</span>}
          </button>
        );
      })}

      {items.length === 0 && (
        <div className="grid-empty">
          {searchText.trim() ? 'ไม่พบรายการสินค้า' : 'หมวดนี้ยังไม่มีสินค้า'}
        </div>
      )}
    </div>
  );
}
